import { SPACE_COLORS, SPACE_ICONS, type ChatSpace } from "@codvero/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Check, Download, Eye, FileText, FolderInput, FolderOpen, MessageSquare, MessageSquareShare, Plus, Trash2, Upload, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { chatsApi } from "@/api/chats.ts";
import { spacesApi } from "@/api/spaces.ts";
import { useConfirm } from "@/ui/ConfirmDialog.tsx";
import { Alert, Button, Field, Input, PageTitle, Spinner } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";
import { cn } from "@/lib/cn.ts";
import { NewChatComposer, usePersonalSpace } from "./SpacePage.tsx";
import { DEFAULT_SPACE_COLOR, SPACE_ICON_COMPONENTS, SpaceBadge, logoFromFile } from "./SpaceBadge.tsx";
import { FilePreview, saveToDevice } from "@/features/chat/FilePreview.tsx";
import { filesApi } from "@/api/browser.ts";
import { ProviderIcon } from "@/ui/ProviderIcon.tsx";

const fmtSize = (n: number) => (n < 1024 ? `${n} B` : n < 1048576 ? `${Math.round(n / 1024)} KB` : `${(n / 1048576).toFixed(1)} MB`);

/** /chat/spaces: every space, like Claude's projects list. */
export function SpacesList() {
  const { t, i18n } = useTranslation();
  const q = useQuery({ queryKey: ["chat-spaces"], queryFn: spacesApi.list });
  const [creating, setCreating] = useState(false);
  const fmt = (iso: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(new Date(iso));
  return (
    <div className="space-y-4">
      <PageTitle title={t("spaces.title")} subtitle={t("spaces.subtitle")} />
      <div className="flex justify-end"><Button onClick={() => setCreating(true)}><Plus className="size-4" /> {t("spaces.new")}</Button></div>
      {q.isLoading && <div className="grid place-items-center py-10"><Spinner /></div>}
      {q.data?.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-fg-muted"><FolderOpen className="mx-auto mb-2 size-6" />{t("spaces.empty")}</div>
      )}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {(q.data ?? []).map((s) => (
          <Link key={s.id} to="/chat/spaces/$spaceId" params={{ spaceId: s.id }} className="flex flex-col rounded-xl border border-border bg-surface p-4 hover:border-accent/60">
            <div className="flex items-center gap-2 font-semibold"><SpaceBadge space={s} size="md" /><span className="truncate">{s.name}</span></div>
            <p dir="auto" className="mt-2 line-clamp-3 min-h-[3.75rem] text-xs text-fg-muted">{s.instructions || t("spaces.noInstructions")}</p>
            <div className="mt-3 flex items-center gap-3 text-[11px] text-fg-muted">
              <span className="flex items-center gap-1"><MessageSquare className="size-3" /> {t("spaces.chats", { count: s.chatCount })}</span>
              <span className="ms-auto">{fmt(s.updatedAt)}</span>
            </div>
          </Link>
        ))}
      </div>
      {creating && <SpaceDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

/** Create or rename a space and edit its instructions (a dialog over the page). */
function SpaceDialog({ space, onClose }: { space?: ChatSpace; onClose: () => void }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [name, setName] = useState(space?.name ?? "");
  const [instructions, setInstructions] = useState(space?.instructions ?? "");
  const [color, setColor] = useState<string>(space?.color ?? DEFAULT_SPACE_COLOR);
  const [icon, setIcon] = useState<string>(space?.icon ?? "folder");
  const [logo, setLogo] = useState<string | null>(space?.logo ?? null);
  const [logoErr, setLogoErr] = useState(false);
  const logoRef = useRef<HTMLInputElement>(null);
  const look = { color, icon: icon as (typeof SPACE_ICONS)[number], logo };
  const save = useMutation({
    mutationFn: () => (space ? spacesApi.update(space.id, { name: name.trim(), instructions, ...look }) : spacesApi.create({ name: name.trim(), instructions, ...look })),
    onSuccess: (s) => {
      void qc.invalidateQueries({ queryKey: ["chat-spaces"] });
      onClose();
      if (!space) void nav({ to: "/chat/spaces/$spaceId", params: { spaceId: s.id } });
    },
  });
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={onClose} onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}>
      <form role="dialog" aria-modal="true" aria-label={space ? t("spaces.edit") : t("spaces.new")} className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg border border-border bg-surface shadow-xl" onClick={(e) => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); if (name.trim()) save.mutate(); }}>
        <div className="flex items-start justify-between border-b border-border p-4">
          <div><h2 className="font-semibold">{space ? t("spaces.edit") : t("spaces.new")}</h2><p className="text-xs text-fg-muted">{t("spaces.dialogHint")}</p></div>
          <button type="button" aria-label={t("common.close")} onClick={onClose} className="rounded p-1 hover:bg-surface-2"><X className="size-5" /></button>
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          <Field label={t("spaces.name")}>
            <div className="flex items-center gap-2">
              <SpaceBadge space={{ name, ...look }} size="lg" />
              <Input autoFocus required dir="auto" maxLength={80} placeholder={t("spaces.namePlaceholder")} value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          </Field>
          <fieldset className="space-y-3 rounded-lg border border-border p-3">
            <legend className="px-1 text-xs font-medium text-fg-muted">{t("spaces.look")}</legend>
            <div className="space-y-1.5">
              <p className="text-xs text-fg-muted">{t("spaces.color")}</p>
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t("spaces.color")}>
                {SPACE_COLORS.map((c) => (
                  <button key={c} type="button" role="radio" aria-checked={color === c} aria-label={c} onClick={() => setColor(c)} className={cn("size-7 rounded-full ring-offset-2 ring-offset-surface", color === c && "ring-2 ring-fg")} style={{ backgroundColor: c }} />
                ))}
              </div>
            </div>
            <div className={cn("space-y-1.5", logo && "opacity-50")}>
              <p className="text-xs text-fg-muted">{t("spaces.icon")}</p>
              <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={t("spaces.icon")}>
                {SPACE_ICONS.map((k) => {
                  const Icon = SPACE_ICON_COMPONENTS[k]!;
                  return (
                    <button key={k} type="button" role="radio" aria-checked={icon === k} aria-label={k} onClick={() => setIcon(k)} className={cn("grid size-8 place-items-center rounded-md border", icon === k ? "border-transparent" : "border-border text-fg-muted hover:text-fg")} style={icon === k ? { backgroundColor: `${color}26`, color } : undefined}>
                      <Icon className="size-4" />
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs text-fg-muted">{t("spaces.logo")} · {t("spaces.logoHint")}</p>
              <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (!f) return; setLogoErr(false); try { setLogo(await logoFromFile(f)); } catch { setLogoErr(true); } }} />
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" size="sm" variant="secondary" onClick={() => logoRef.current?.click()}><Upload className="size-3.5" /> {t("spaces.uploadLogo")}</Button>
                {logo && <Button type="button" size="sm" variant="ghost" onClick={() => setLogo(null)}><X className="size-3.5" /> {t("spaces.removeLogo")}</Button>}
              </div>
              {logoErr && <p className="text-xs text-danger">{t("spaces.badLogo")}</p>}
            </div>
          </fieldset>
          <Field label={t("spaces.instructions")} hint={t("spaces.instructionsHint")}>
            <textarea dir="auto" rows={8} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder={t("spaces.instructionsPlaceholder")} className="block w-full resize-y rounded-md border border-border bg-bg px-3 py-2 text-sm focus:border-accent focus:outline-none" />
          </Field>
          {save.error && <Alert tone="danger">{toMsg(save.error)}</Alert>}
        </div>
        <div className="flex justify-end gap-2 border-t border-border p-4"><Button type="button" variant="ghost" onClick={onClose}>{t("common.cancel")}</Button><Button type="submit" loading={save.isPending} disabled={!name.trim()}>{t("common.save")}</Button></div>
      </form>
    </div>
  );
}

/** /chat/spaces/$spaceId: start a conversation in the space, its conversations, its instructions and files. */
export function SpaceDetail({ spaceId }: { spaceId: string }) {
  const { t, i18n } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { confirm } = useConfirm();
  const personal = usePersonalSpace().data;
  const q = useQuery({ queryKey: ["chat-spaces", spaceId], queryFn: () => spacesApi.get(spaceId) });
  const files = useQuery({ queryKey: ["chat-spaces", spaceId, "files"], queryFn: () => spacesApi.files(spaceId), refetchInterval: (x) => (x.state.data ? false : 3000) });
  const chats = useQuery({ queryKey: ["chats", personal?.id, "list"], queryFn: () => chatsApi.list(personal!.id), enabled: !!personal });
  const inSpace = useMemo(() => (chats.data ?? []).filter((c) => c.spaceId === spaceId && !c.archived), [chats.data, spaceId]);
  const [editing, setEditing] = useState(false);
  const [opened, setOpened] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(0);
  const [uploadErr, setUploadErr] = useState<unknown>(null);
  const refreshFiles = () => void qc.invalidateQueries({ queryKey: ["chat-spaces", spaceId, "files"] });
  const addFiles = async (list: FileList) => {
    setUploadErr(null);
    for (const f of Array.from(list)) {
      setUploading((n) => n + 1);
      try { await spacesApi.upload(spaceId, f); } catch (e) { setUploadErr(e); } finally { setUploading((n) => n - 1); }
    }
    refreshFiles();
  };
  const removeFile = useMutation({ mutationFn: (name: string) => spacesApi.removeFile(spaceId, name), onSuccess: refreshFiles });
  const [tab, setTab] = useState<"mine" | "outputs">("mine");
  const unhide = useMutation({ mutationFn: () => spacesApi.setHidden(spaceId, false), onSuccess: () => void qc.invalidateQueries({ queryKey: ["chat-spaces"] }) });
  const outputs = useQuery({ queryKey: ["chat-spaces", spaceId, "outputs"], queryFn: () => spacesApi.outputs(spaceId), staleTime: 30_000 });
  const keep = useMutation({ mutationFn: (path: string) => spacesApi.keepOutput(spaceId, path), onSuccess: () => { refreshFiles(); void qc.invalidateQueries({ queryKey: ["chat-spaces", spaceId, "outputs"] }); } });
  const remove = useMutation({ mutationFn: () => spacesApi.remove(spaceId), onSuccess: () => { void qc.invalidateQueries({ queryKey: ["chat-spaces"] }); void nav({ to: "/chat/spaces" }); } });
  useEffect(() => { if (q.data) document.title = `${q.data.name} · Codvero`; }, [q.data?.name]);
  const fmt = (iso: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
  if (q.isError) return <Alert tone="danger">{toMsg(q.error)}</Alert>;
  if (!q.data) return <div className="grid place-items-center py-10"><Spinner /></div>;
  const s = q.data;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <Link to="/chat/spaces" className="text-xs text-fg-muted hover:text-fg">{t("spaces.title")}</Link>
          <h1 className="flex items-center gap-2 truncate text-xl font-semibold"><SpaceBadge space={s} size="md" /> {s.name}</h1>
        </div>
        {s.hidden && <Button variant="secondary" size="sm" loading={unhide.isPending} onClick={() => unhide.mutate()}><Eye className="size-4" /> {t("spaces.showInSidebar")}</Button>}
        <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>{t("spaces.edit")}</Button>
        <Button variant="ghost" size="sm" loading={remove.isPending} onClick={async () => { if (await confirm({ title: t("spaces.deleteTitle"), message: t("spaces.deleteMsg", { name: s.name }), confirmLabel: t("common.delete"), danger: true })) remove.mutate(); }}><Trash2 className="size-4" /></Button>
      </div>
      {remove.error && <Alert tone="danger">{toMsg(remove.error)}</Alert>}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-4">
          <NewChatComposer spaceId={s.id} placeholder={t("spaces.composerPlaceholder", { name: s.name })} />
          <section className="space-y-2">
            <h3 className="text-sm font-semibold">{t("spaces.chatsTitle")}</h3>
            {inSpace.length === 0 && <p className="rounded-lg border border-dashed border-border p-4 text-center text-xs text-fg-muted">{t("spaces.noChats")}</p>}
            <ul className="divide-y divide-border rounded-lg border border-border bg-surface">
              {inSpace.map((c) => (
                <li key={c.id}><Link to="/chat/$chatId" params={{ chatId: c.id }} className="flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-surface-2"><MessageSquare className="size-4 shrink-0 text-fg-muted" /><span dir="auto" className="min-w-0 flex-1 truncate">{c.title}</span><span className="shrink-0 text-[11px] text-fg-muted">{fmt(c.updatedAt)}</span></Link></li>
              ))}
            </ul>
          </section>
        </div>

        <aside className="min-w-0 space-y-4">
          <section className="rounded-lg border border-border bg-surface p-4">
            <div className="mb-2 flex items-center justify-between"><h3 className="text-sm font-semibold">{t("spaces.instructions")}</h3><button type="button" className="text-xs text-accent hover:underline" onClick={() => setEditing(true)}>{t("common.edit")}</button></div>
            <p dir="auto" className="whitespace-pre-wrap text-xs leading-6 text-fg-muted">{s.instructions || t("spaces.noInstructions")}</p>
          </section>
          <SpaceMemoryCard spaceId={s.id} />
          <section className="rounded-lg border border-border bg-surface p-4">
            {/* two lists: the user's own reference files, and what the providers made in the space's conversations */}
            <div className="mb-3 flex items-center gap-2">
              <div role="tablist" className="flex min-w-0 flex-1 gap-1 rounded-lg bg-surface-2 p-0.5">
                {(["mine", "outputs"] as const).map((k) => (
                  <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className={cn("min-w-0 flex-1 truncate rounded-md px-2 py-1 text-xs font-medium", tab === k ? "bg-surface text-fg shadow-sm" : "text-fg-muted hover:text-fg")}>
                    {t(`spaces.tabs.${k}`)}{(k === "mine" ? files.data : outputs.data) ? ` (${(k === "mine" ? files.data : outputs.data)!.length})` : ""}
                  </button>
                ))}
              </div>
              <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { if (e.target.files) void addFiles(e.target.files); e.target.value = ""; }} />
              {tab === "mine" && <Button size="sm" variant="secondary" loading={uploading > 0} onClick={() => fileRef.current?.click()}><Upload className="size-3.5" /> {t("spaces.upload")}</Button>}
            </div>
            {tab === "mine" ? (
              <>
                <p className="mb-2 text-[11px] text-fg-muted">{t("spaces.filesHint")}</p>
                {(uploadErr || removeFile.error) && <Alert tone="danger" className="mb-2">{toMsg(uploadErr ?? removeFile.error)}</Alert>}
                {files.data?.length === 0 && <p className="text-center text-xs text-fg-muted">{t("spaces.noFiles")}</p>}
                <ul className="space-y-1">
                  {(files.data ?? []).map((f) => (
                    <li key={f.name} className="flex items-center gap-2 rounded-md px-1 py-1 text-xs hover:bg-surface-2">
                      <FileText className="size-3.5 shrink-0 text-fg-muted" />
                      <button type="button" dir="auto" className="min-w-0 flex-1 truncate text-start hover:underline" onClick={() => setOpened(`${s.filesDir}/${f.name}`)}>{f.name}</button>
                      <span className="shrink-0 text-fg-muted">{fmtSize(f.size)}</span>
                      <button type="button" aria-label={t("common.remove")} className="text-fg-muted hover:text-danger" onClick={async () => { if (await confirm({ title: t("spaces.removeFileTitle"), message: t("spaces.removeFileMsg", { name: f.name }), confirmLabel: t("common.delete"), danger: true })) removeFile.mutate(f.name); }}><X className="size-3.5" /></button>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <>
                <p className="mb-2 text-[11px] text-fg-muted">{t("spaces.outputsHint")}</p>
                {(outputs.error || keep.error) && <Alert tone="danger" className="mb-2">{toMsg(outputs.error ?? keep.error)}</Alert>}
                {outputs.isLoading && <div className="grid place-items-center py-3"><Spinner /></div>}
                {outputs.data?.length === 0 && <p className="text-center text-xs text-fg-muted">{t("spaces.noOutputs")}</p>}
                <ul className="space-y-1">
                  {(outputs.data ?? []).map((o) => (
                    <li key={o.path} className="rounded-md px-1 py-1.5 text-xs hover:bg-surface-2">
                      <div className="flex items-center gap-2">
                        {o.provider ? <ProviderIcon provider={o.provider} size="xs" /> : <FileText className="size-3.5 shrink-0 text-fg-muted" />}
                        <button type="button" dir="auto" title={o.path} className="min-w-0 flex-1 truncate text-start font-medium hover:underline" onClick={() => setOpened(o.path)}>{o.name}</button>
                        <span className="shrink-0 text-fg-muted">{fmtSize(o.size)}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-1 ps-6">
                        <span dir="auto" className="min-w-0 flex-1 truncate text-[11px] text-fg-muted">{o.chatTitle} · {fmt(o.modifiedAt)}</span>
                        <button type="button" aria-label={t("spaces.download")} title={t("spaces.download")} className="tap grid place-items-center rounded p-1 text-fg-muted hover:text-fg" onClick={() => personal && void saveToDevice(filesApi.rawUrl(personal.id, o.path, true), o.name, "")}><Download className="size-3.5" /></button>
                        <Link to="/chat/$chatId" params={{ chatId: o.openChatId }} {...(o.turnSeq === null ? {} : { hash: o.openChatId === o.chatId ? `turn-${o.turnSeq}` : `seg-${o.chatId}-${o.turnSeq}` })} aria-label={t("spaces.goToChat")} title={t("spaces.goToChat")} className="tap grid place-items-center rounded p-1 text-fg-muted hover:text-fg"><MessageSquareShare className="size-3.5" /></Link>
                        {o.inMyFiles
                          ? <span title={t("spaces.inMyFiles")} aria-label={t("spaces.inMyFiles")} className="grid place-items-center p-1 text-success"><Check className="size-3.5" /></span>
                          : <button type="button" aria-label={t("spaces.keep")} title={t("spaces.keep")} disabled={keep.isPending} className="tap grid place-items-center rounded p-1 text-fg-muted hover:text-fg disabled:opacity-50" onClick={() => keep.mutate(o.path)}>{keep.isPending && keep.variables === o.path ? <Spinner className="size-3.5" /> : <FolderInput className="size-3.5" />}</button>}
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
        </aside>
      </div>
      {opened && personal && <FilePreview projectId={personal.id} path={opened} onClose={() => setOpened(null)} />}
      {editing && <SpaceDialog space={s} onClose={() => { setEditing(false); void qc.invalidateQueries({ queryKey: ["chat-spaces", spaceId] }); }} />}
    </div>
  );
}

/** The space's memory (shared by its conversations), shown and edited beside its instructions. */
function SpaceMemoryCard({ spaceId }: { spaceId: string }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const key = ["chat-spaces", spaceId, "memory"];
  const q = useQuery({ queryKey: key, queryFn: () => spacesApi.memory(spaceId) });
  const [draft, setDraft] = useState<string | null>(null);
  const save = useMutation({ mutationFn: (content: string) => spacesApi.saveMemory(spaceId, content), onSuccess: (d) => { qc.setQueryData(key, d); setDraft(null); } });
  const value = draft ?? q.data?.content ?? "";
  const dirty = draft !== null && draft !== (q.data?.content ?? "");
  return (
    <section className="rounded-lg border border-border bg-surface p-4">
      <div className="mb-1 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{t("chatMemory.spaceCard")}</h3>
        <Button size="sm" variant="secondary" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate(value)}>{t("common.save")}</Button>
      </div>
      <p className="mb-2 text-[11px] text-fg-muted">{t("chatMemory.spaceCardHint")}</p>
      {(q.error || save.error) && <Alert tone="danger" className="mb-2">{toMsg(q.error ?? save.error)}</Alert>}
      {q.isLoading ? <div className="grid place-items-center py-3"><Spinner /></div> : (
        <textarea dir="auto" rows={7} value={value} onChange={(e) => setDraft(e.target.value)} maxLength={32768} placeholder={t("memory.placeholder")} className="block w-full resize-y rounded-md border border-border bg-bg px-3 py-2 font-mono text-xs leading-5 focus:border-accent focus:outline-none" />
      )}
    </section>
  );
}
